import * as _ from 'lodash';

import { ActiveFilterModel } from './active-filters/active-filter.model';
import { YEAR_START_TYPE, YEAR_END_TYPE } from './advanced-search/filter-types';
import { VEHICLE_FILTER_TYPE, VCDB_FILTER_TYPE, PART_ATTRIBUTES_FILTER_TYPE } from './active-filters/active-filter-types';
import { allOptionValue } from '../form/select/option-helpers';


/*
  * Returns object with key = filter type, value = option
*/
export const getVehicleFiltersMap = (vehicleFilters: Array<ActiveFilterModel>): any => {
  const obj = _.reduce(vehicleFilters, (result: any, activeFilter: any) => {
    if (activeFilter.data.multiple) {
      const optionsArr = _.uniq(_.map(activeFilter.data.options, 'option'));
      const optionType = _.get(activeFilter, 'data.options[0].type');
      _.set(result, optionType, optionsArr);
    } else {
      _.each(activeFilter.data.options, (option: any) => {
        _.set(result, option.type, option.option || '');
      });
    }
    return result;
  }, {});
  if (_.has(obj, YEAR_START_TYPE)) {
    if (_.has(obj, YEAR_END_TYPE)) { return obj; }
    // if minYear was specified without maxYear, then add maxYear === minYear
    _.set(obj, YEAR_END_TYPE, obj[YEAR_START_TYPE]);
  }
  return obj;
};

/*
  * Returns object with key = attribute name, value = attribute option
*/
export const getVcdbFiltersMap = (vcdbFilters: Array<ActiveFilterModel>): any => {
  return _.reduce(vcdbFilters, (result: any, activeFilter: any) => {
    const attributeName = _.get(activeFilter, 'data.attributeInfo.attribute_name');
    const attributeOptions = _.chain(activeFilter).get('data.options').map('option').value();
    _.set(result, attributeName, attributeOptions);
    return result;
  }, {});
};

/*
  * Modify filters into array of key/value object:
    [{
      _id: activeFilterId, // technical field for removing filter
      _title: activeFilterTitle, // technical field for grouping filters
      filterName1: value1,
      filterName2: value1,
      ...
    }, ...]
*/
// function-helper to handle setting values array
const setPartAttributeFilterFieldMap = (result: any, activeFilter: ActiveFilterModel, fieldName: string, value: any, index: number) => {
  if (!_.get(result, `[${index}]`)) {
    result[index] = {};
  }
  result[index][fieldName] = value;
  result[index]._id = activeFilter._id;
  result[index]._group = activeFilter.group;
  result[index]._type = activeFilter.type;
};

export const getPartAttributesFiltersMap = (partAttributesFilters: Array<ActiveFilterModel>): any => {
  return _.reduce(partAttributesFilters, (result: any, activeFilter: any, index: number) => {
    result[index] = {};
    if (activeFilter.data.checkNullValue && !activeFilter.data.is_text_select && !activeFilter.data.nested_fields) {
      setPartAttributeFilterFieldMap(result, activeFilter, `${activeFilter.data.attribute_name}_checkNull`, 'Y', index);
      return result;
    } else if (activeFilter.data.langValue && activeFilter.data.langValue !== allOptionValue) {
      // eslint-disable-next-line max-len
      setPartAttributeFilterFieldMap(result, activeFilter, `${activeFilter.data.attribute_name}_checkLang`, `${activeFilter.data.langValue}`, index);
    }

    if (activeFilter.data.is_range && activeFilter.data.date_picker) {
      const dateFromValueUTC = activeFilter.data.dateFromValueUTC ? activeFilter.data.dateFromValueUTC : activeFilter.data.dateFromValue;
      const dateToValueUTC = activeFilter.data.dateToValueUTC ? activeFilter.data.dateToValueUTC : activeFilter.data.dateToValue;
      if (dateFromValueUTC) {
        setPartAttributeFilterFieldMap(result, activeFilter, `${activeFilter.data.from_field}`, `${dateFromValueUTC}`, index);
      }
      if (dateToValueUTC) {
        setPartAttributeFilterFieldMap(result, activeFilter, `${activeFilter.data.to_field}`, `${dateToValueUTC}`, index);
      }
      return result;
    } else if (activeFilter.data.is_text_input_only) {
      setPartAttributeFilterFieldMap(result, activeFilter, `${activeFilter.data.attribute_name}`, `${activeFilter.data.textValue}`, index);
      return result;
    } else if (activeFilter.data.nestedAttributes && activeFilter.data.nestedAttributes.length) {
      if (activeFilter.data.checkNullValue) {
        for (let i = 0; i < activeFilter.data.nestedAttributes.length; i++) {
          const nestedAttribute = activeFilter.data.nestedAttributes[i];
          if (nestedAttribute.selectValue && i !== activeFilter.data.nestedAttributes.length - 1) {
            // eslint-disable-next-line max-len
            setPartAttributeFilterFieldMap(result, activeFilter, `${nestedAttribute.attribute.attribute_name}`, `${nestedAttribute.selectValue}`, index);
          } else {
            setPartAttributeFilterFieldMap(result, activeFilter, `${nestedAttribute.attribute.attribute_name}_checkNull`, 'Y', index);
            break;
          }
        }
        return result;
      } else {
        _.forEach(activeFilter.data.nestedAttributes, (nestedAttribute: any) => {
          if (!nestedAttribute.selectValue) {
            return;
          }
          // eslint-disable-next-line max-len
          setPartAttributeFilterFieldMap(result, activeFilter, `${nestedAttribute.attribute.attribute_name}`, `${nestedAttribute.selectValue}`, index);
        });
        return result;
      }
    } else {
      const selectName = activeFilter.data.select_field_name ? activeFilter.data.select_field_name : activeFilter.data.attribute_name;
      if (activeFilter.data.selectValue !== allOptionValue) {
        setPartAttributeFilterFieldMap(result, activeFilter, `${selectName}`, `${activeFilter.data.selectValue}`, index);
      }
      if (activeFilter.data.is_text_select) {
        if (activeFilter.data.checkNullValue) {
          setPartAttributeFilterFieldMap(result, activeFilter, `${activeFilter.data.attribute_name}_checkNull`, `Y`, index);
        } else {
          setPartAttributeFilterFieldMap(result, activeFilter, `${activeFilter.data.text_field_name}`, activeFilter.data.textValue, index);
        }
      }
      return result;
    }
  }, []);
};

export const getUserAttributesFiltersMap = (padbAttributesFilters: Array<ActiveFilterModel>): any => {
  return _.reduce(padbAttributesFilters, (result: any, activeFilter: any, index: number) => {
    result[index] = {};

    if (activeFilter.data.checkNullValue) {
      setPartAttributeFilterFieldMap(result, activeFilter, 'checkNull', 'Y', index);
      return result;
    }
    setPartAttributeFilterFieldMap(result, activeFilter, `${activeFilter.data.selectValue}`, activeFilter.data.textValue, index);
    return result;
  }, []);
};

export const getAllFiltersMap = (filters: Array<ActiveFilterModel>): any => {
  const filterGroups = _.groupBy(filters, 'type');
  return _.assign(
    getVehicleFiltersMap(filterGroups[VEHICLE_FILTER_TYPE]),
    getVcdbFiltersMap(filterGroups[VCDB_FILTER_TYPE]),
    getPartAttributesFiltersMap(filterGroups[PART_ATTRIBUTES_FILTER_TYPE]),
  );
};

export const getFilterPostObject = (filters: Array<any>): any => {
  const filtersObj = getAllFiltersMap(filters);

  return _.reduce(filtersObj, (result: any, values: any, key: string) => {
    if (!_.has(result, values._group)) {
      _.set(result, values._group, []);
    }
    result[values._group].push(values[values._group]);
    return result;
  }, []);
};

export const getFiltersQueryString = (vehicleFilters: Array<ActiveFilterModel>): string => {
  const filtersObj = getVehicleFiltersMap(vehicleFilters);
  return encodeURI(_.reduce(filtersObj, (result: string, value: any, key: string) => {
    return `${result}&${key}=${JSON.stringify(value)}`;
  }, ''));
};

export const getVcdbFiltersQueryString = (vcdbFilters: Array<ActiveFilterModel>): string => {
  const vcdbFiltersObj = getVcdbFiltersMap(vcdbFilters);
  return encodeURI(_.reduce(vcdbFiltersObj, (result: string, value: any, key: string) => {
    return `${result}&${key}=${JSON.stringify(value)}`;
  }, ''));
};

export const getFiltersGroupsQueryString = (vehicleFilters: Array<ActiveFilterModel>): string => {
  const filtersObj = getVehicleFiltersMap(vehicleFilters);
  let string = '';
  _.each(filtersObj, (filterValue: any, key: string) => {
    if (_.isArray(filterValue)) {
      _.each(filterValue, (value: any) => {
        string += `&filter[groups][0][${key}][]=${value}`;
      });
    } else {
      string += `&filter[groups][0][${key}]=${filterValue}`;
    }
  });
  return string;
};

export const getVcdbFiltersGroupsQueryString = (vcdbFilters: Array<ActiveFilterModel>): string => {
  const vcdbFiltersObj = getVcdbFiltersMap(vcdbFilters);
  return _.reduce(vcdbFiltersObj, (result: string, values: Array<any>, key: string) => {
    _.each(values, (value: any) => {
      result = `${result}&filter[groups][0][${key}][]=${value}`;
    });
    return result;
  }, '');
};

export const getPartAttributesFiltersQueryString = (partAttributesFilters: Array<ActiveFilterModel>): string => {
  const filtersMap = _.groupBy(getPartAttributesFiltersMap(partAttributesFilters), '_group');
  /*
    {
      'partType': [{
        '_id': 'partType-0',
        '_group': 'partType',
        'partType': '123',
      }, {
        '_id': 'partType-1',
        '_group': 'partType',
        'partType': '345',
      }],
      ...
    }
  */
  let result = '';
  _.forEach(_.keys(filtersMap), (key: string) => {
    _.forEach(filtersMap[key], (filter: any, filterIndex: number) => {
      result = _.reduce(_.keys(filter), (queryString: string, filterName: any) => {
        if (filterName === '_id' || filterName === '_group' || filterName === '_type') {
          return queryString;
        }
        return `${queryString}&filter[${filterName}][${filterIndex}]=${filter[filterName]}`;
      }, result);
    });
  });

  return result;
};

export const getUserAttributesFiltersQueryString = (userAttributesFilters: Array<ActiveFilterModel>, isPadb: boolean): string => {
  const filtersMap = _.groupBy(getUserAttributesFiltersMap(userAttributesFilters), '_group');
  // const prefix = isPadb ? 'padb' : 'custom';
  /*
    {
      'partType': [{
        '_id': 'partType-0',
        '_group': 'partType',
        '_type': 'custom'/'padb',
        'partType': '123',
      }, {
        '_id': 'partType-1',
        '_group': 'partType',
        '_type': 'custom'/'padb',
        'partType': '345',
      }],
      ...
    }
  */

  let result = '';

  _.forEach(_.keys(filtersMap), (key: string, attrIndex: number) => {
    _.forEach(filtersMap[key], (filter: any) => {

      result = _.reduce(_.keys(filter), (queryString: string, filterName: any) => {
        if (filterName === '_id' || filterName === '_group' || filterName === '_type') {
          return queryString;
        }

        let qs = `${queryString}&filter[productAttribute_checkType][${attrIndex}]=${filter._type === 'padb' ? 'Y' : 'N'}`;
        qs = `${qs}&filter[productAttribute_checkSelect][${attrIndex}]=${key}`;
        if (filterName === 'checkNull') {
          // eslint-disable-next-line max-len
          return `${qs}&filter[productAttribute_checkNull][${attrIndex}]=${filter[filterName]}`;
        } else {
          // eslint-disable-next-line max-len
          if (filter[filterName]) {
            return `${qs}&filter[productAttribute_checkText][${attrIndex}]=${filter[filterName]}`;
          }
          return qs;
        }
      }, result);
    });
  });

  return result;
};

export const getSearchParamsFromQuery = (searchQuery: string): any => {
  return _.chain(searchQuery)
  .split('&')
  .map((item) => _.split(item, '='))
  .fromPairs()
  .forOwn((value, key, obj) => {
    try {
      obj[key] = JSON.parse(decodeURI(value));
    } catch (err) {
      obj[key] = value;
    }
  })
  .value();
};

export const advancedSearchRouteMap: any = {
  'working': 'working',
  'published': 'published',
};

export const getColWidth = (col: any, columns: any[]) => {
  if (col.width) { return col.width; }
  if (col.cssWidth) { return col.cssWidth; }
  let res = '100%';
  let colWithoutWidthCount = 0;
  res = _.reduce(columns, (memo: any, column: any) => {
    if (column !== col && column.cssWidth) {
      memo = `${memo} - ${column.cssWidth}`;
    } else {
      colWithoutWidthCount += 1;
    }
    return memo;
  }, res);

  return `calc((${res}) / ${colWithoutWidthCount})`;
};


/*
  * Convert data from object to array, filter groups without attributes
*/
export const prepareGroupedFieldsList = (data: any): Array<any> => {

    return _.chain(data)
  .values()
   // @ts-ignore
  .filter((item: any) => _.size(item.fields))
  .map((item: any) => {
    item.fields = _.values(item.fields);
    return item;
  })
  .value();
};
